import { z } from "zod";
import { IsoDateTimeSchema } from "./primitives.js";
import { PublicAppointmentTokenSchema } from "./scheduling.js";
/**
 * Health card photos (PA-13 / EXECDEV-114): a photo of the front of the patient's health card,
 * taken on the patient's phone in the questionnaire (scheduled and walk-in links) or by staff at
 * registration and on the chart. Capture only: nothing reads the card (no OCR, no model), and the
 * typed number stays the record. A retake replaces the earlier photo.
 *
 * The browser downscales and re-encodes the photo (long edge clientLongEdgePx, JPEG quality
 * clientJpegQuality), drops its metadata with stripJpegMetadata and checks it with
 * inspectHealthCardJpeg. The API checks the bytes again with inspectHealthCardJpeg and refuses
 * anything else (413 too large, 415 any other issue; nothing is stored). Accepted bytes go to a
 * private quarantine bucket; the worker's photos lane scans them, and staff see a photo only once
 * it is clean, through an audited proxy that never caches.
 *
 * Patient route (public lane; credentials only in the body):
 *
 * - POST /v1/public/:clinicSlug/intake/health-card-photo
 *     PublicHealthCardPhotoUploadRequestSchema -> PublicHealthCardPhotoReceiptSchema.
 *     409 PUBLIC_HEALTH_CARD_PHOTOS_UNAVAILABLE_ERROR_CODE: the clinic is not taking photos now
 *     (hide the photo buttons for the session; the patient types the details).
 *     409 PUBLIC_HEALTH_CARD_PHOTO_LIMIT_ERROR_CODE: this link sent maxPerLink photos.
 *     A wrong date of birth is the generic 404 and counts toward the link's lockout.
 *
 * Staff routes (scheduling staff scope, the signed-in member's site; the registration roles):
 *
 * - POST /v1/patients/:patientId/health-card-photos
 *     raw HEALTH_CARD_PHOTO_MEDIA_TYPE body (no JSON, no base64) -> HealthCardPhotoSchema
 * - GET  /v1/patients/:patientId/health-card-photos                    HealthCardPhotoListSchema
 * - GET  /v1/scheduling/appointments/:appointmentId/health-card-photos HealthCardPhotoListSchema
 * - GET  /v1/scheduling/health-card-photos/:photoId/content
 *     image/jpeg bytes: clean photos only, audited, Cache-Control private, no-store
 * - POST /v1/scheduling/health-card-photos/:photoId/delete -> HealthCardPhotoDeleteReceiptSchema
 * - GET  /v1/scheduling/health-card-photo-settings                    HealthCardPhotoSettingsSchema
 * - POST /v1/scheduling/health-card-photo-settings (clinic_admin)
 *     SaveHealthCardPhotoSettingsRequestSchema -> HealthCardPhotoSettingsSchema
 *
 * Staff refusals carry a SchedulingErrorDetail: health_card_photos_unavailable,
 * health_card_photo_limit, health_card_photo_invalid, health_card_photo_not_ready,
 * health_card_photo_settings_changed.
 */
export const HEALTH_CARD_PHOTO_LIMITS = {
    /** The largest photo accepted, in bytes (also the bucket's file size limit). */
    maxBytes: 2_097_152,
    /** Each edge of an accepted photo, in pixels. */
    minEdgePx: 320,
    maxEdgePx: 2048,
    /** What the browser downscales the photo's long edge to before sending it. */
    clientLongEdgePx: 1600,
    /** The browser's JPEG quality when it re-encodes the photo. */
    clientJpegQuality: 0.85,
    /** Photos one questionnaire link may send (retakes included). */
    maxPerLink: 5,
    /** Rows in one photo list. */
    listMax: 20,
    /** The public upload's JSON body limit: the base64 of maxBytes plus the token and date. */
    bodyLimit: 2_900_000,
};
/** How long a clinic keeps a photo, in days (a clinic setting). */
export const HEALTH_CARD_PHOTO_RETENTION = {
    defaultDays: 90,
    minDays: 1,
    maxDays: 365,
};
/** The staff upload's request body type. */
export const HEALTH_CARD_PHOTO_MEDIA_TYPE = "image/jpeg";
/**
 * The public lane's error code (HTTP 409) when the clinic is not taking photos now (no photo
 * checker is running). Nothing was stored. The patient app hides its photo buttons for the
 * session and the patient types the details instead. Given only after the date of birth matched.
 */
export const PUBLIC_HEALTH_CARD_PHOTOS_UNAVAILABLE_ERROR_CODE = "HEALTH_CARD_PHOTOS_UNAVAILABLE";
/**
 * The public lane's error code (HTTP 409) when the link already sent
 * HEALTH_CARD_PHOTO_LIMITS.maxPerLink photos. Nothing was stored; the last photo sent stands.
 */
export const PUBLIC_HEALTH_CARD_PHOTO_LIMIT_ERROR_CODE = "HEALTH_CARD_PHOTO_LIMIT";
// ---------------------------------------------------------------------------
// Base64 (the public upload carries the photo in its JSON body)
// ---------------------------------------------------------------------------
const BASE64_ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
const BASE64_VALUES = (() => {
    const values = new Int8Array(128).fill(-1);
    for (let index = 0; index < BASE64_ALPHABET.length; index += 1) {
        values[BASE64_ALPHABET.charCodeAt(index)] = index;
    }
    return values;
})();
function base64Value(text, index) {
    const code = text.charCodeAt(index);
    return code < 128 ? (BASE64_VALUES[code] ?? -1) : -1;
}
function base64Padding(text) {
    return text.endsWith("==") ? 2 : text.endsWith("=") ? 1 : 0;
}
/**
 * Whether the text is canonical, padded RFC 4648 base64 (standard alphabet, no whitespace, no
 * line breaks, unused bits zero) and not empty. Node's Buffer.from(text, "base64") silently skips
 * characters it does not know, so a server checks this first.
 */
export function isStrictBase64(text) {
    if (text.length === 0 || text.length % 4 !== 0)
        return false;
    const padding = base64Padding(text);
    const dataLength = text.length - padding;
    for (let index = 0; index < dataLength; index += 1) {
        if (base64Value(text, index) < 0)
            return false;
    }
    if (padding === 0)
        return true;
    const last = base64Value(text, dataLength - 1);
    return (last & (padding === 2 ? 0x0f : 0x03)) === 0;
}
/** Canonical, padded base64 of the bytes (browsers and servers alike; no Buffer, no btoa). */
export function encodeStrictBase64(bytes) {
    let text = "";
    for (let index = 0; index < bytes.length; index += 3) {
        const second = bytes[index + 1];
        const third = bytes[index + 2];
        const triple = ((bytes[index] ?? 0) << 16) | ((second ?? 0) << 8) | (third ?? 0);
        text += BASE64_ALPHABET.charAt((triple >> 18) & 63);
        text += BASE64_ALPHABET.charAt((triple >> 12) & 63);
        text += second === undefined ? "=" : BASE64_ALPHABET.charAt((triple >> 6) & 63);
        text += third === undefined ? "=" : BASE64_ALPHABET.charAt(triple & 63);
    }
    return text;
}
/** The bytes of strict base64 text (isStrictBase64), or null for anything else. */
export function decodeStrictBase64(text) {
    if (!isStrictBase64(text))
        return null;
    const padding = base64Padding(text);
    const bytes = new Uint8Array((text.length / 4) * 3 - padding);
    let offset = 0;
    for (let index = 0; index < text.length; index += 4) {
        const quad = (base64Value(text, index) << 18) |
            (base64Value(text, index + 1) << 12) |
            (Math.max(base64Value(text, index + 2), 0) << 6) |
            Math.max(base64Value(text, index + 3), 0);
        bytes[offset] = (quad >> 16) & 0xff;
        if (offset + 1 < bytes.length)
            bytes[offset + 1] = (quad >> 8) & 0xff;
        if (offset + 2 < bytes.length)
            bytes[offset + 2] = quad & 0xff;
        offset += 3;
    }
    return bytes;
}
/** The base64 text of a photo of at most maxBytes. */
export const HEALTH_CARD_PHOTO_BASE64_MAX_LENGTH = Math.ceil(HEALTH_CARD_PHOTO_LIMITS.maxBytes / 3) * 4;
export const HealthCardPhotoBase64Schema = z
    .string()
    .max(HEALTH_CARD_PHOTO_BASE64_MAX_LENGTH)
    .refine(isStrictBase64, "Use canonical base64");
// ---------------------------------------------------------------------------
// JPEG checks (pure: browsers, agent-api and the worker share them)
// ---------------------------------------------------------------------------
/**
 * Why inspectHealthCardJpeg refused the bytes. The API answers too_large with 413 and every
 * other issue with 415; the worker records the photo as rejected.
 *
 * - too_large: more than maxBytes.
 * - not_jpeg: does not start with a JPEG start-of-image marker (PNG, GIF, text, ...).
 * - malformed: a segment is cut short or inconsistent, a marker is misplaced or padded with
 *   0xFF fill bytes, the frame or scan is missing, or the image never ends.
 * - metadata: an APP1-APP15 segment (Exif, GPS, XMP, ICC, ...), a comment, or an APP0 other than
 *   the plain JFIF header without a thumbnail (JFXX, thumbnails).
 * - unsupported: a segment outside the allow-list (lossless, arithmetic or 12-bit coding, a
 *   second frame, DNL, ...).
 * - dimensions: an edge outside minEdgePx-maxEdgePx.
 * - trailing_data: bytes after the end-of-image marker.
 */
export const HealthCardJpegIssueSchema = z.enum([
    "too_large",
    "not_jpeg",
    "malformed",
    "metadata",
    "unsupported",
    "dimensions",
    "trailing_data",
]);
const SOI = 0xd8;
const EOI = 0xd9;
const SOS = 0xda;
const DQT = 0xdb;
const DRI = 0xdd;
const DHT = 0xc4;
const APP0 = 0xe0;
const COM = 0xfe;
/** Baseline, extended sequential and progressive Huffman frames: what canvas encoders write. */
const FRAME_MARKERS = new Set([0xc0, 0xc1, 0xc2]);
const MALFORMED = { ok: false, issue: "malformed" };
function isRestartMarker(marker) {
    return marker >= 0xd0 && marker <= 0xd7;
}
/** APP1-APP15. */
function isMetadataApp(marker) {
    return marker >= 0xe1 && marker <= 0xef;
}
function uint16(bytes, offset) {
    return ((bytes[offset] ?? 0) << 8) | (bytes[offset + 1] ?? 0);
}
/**
 * Splits a JPEG into its marker segments up to the end-of-image marker. 0xFF fill bytes before a
 * marker are left out of every part, so stripJpegMetadata drops them and inspectHealthCardJpeg
 * refuses them.
 */
function walkJpeg(bytes) {
    if (bytes.length < 4 || bytes[0] !== 0xff || bytes[1] !== SOI || bytes[2] !== 0xff) {
        return { ok: false, issue: "not_jpeg" };
    }
    const parts = [{ marker: SOI, start: 0, end: 2 }];
    let offset = 2;
    let filled = false;
    for (;;) {
        while (bytes[offset] === 0xff && bytes[offset + 1] === 0xff) {
            offset += 1;
            filled = true;
        }
        if (offset + 1 >= bytes.length || bytes[offset] !== 0xff)
            return MALFORMED;
        const marker = bytes[offset + 1] ?? 0;
        if (marker === EOI) {
            parts.push({ marker, start: offset, end: offset + 2 });
            return { ok: true, parts, end: offset + 2, filled };
        }
        // Stand-alone markers belong only inside a scan (restarts) or at the start (SOI).
        if (marker === 0x00 || marker === 0x01 || marker === SOI)
            return MALFORMED;
        if (isRestartMarker(marker) || offset + 4 > bytes.length)
            return MALFORMED;
        const length = uint16(bytes, offset + 2);
        let end = offset + 2 + length;
        if (length < 2 || end > bytes.length)
            return MALFORMED;
        if (marker === SOS) {
            // Entropy-coded data runs to the next marker other than a stuffed 0xFF00 or a restart.
            for (;;) {
                end = bytes.indexOf(0xff, end);
                if (end < 0 || end + 1 >= bytes.length)
                    return MALFORMED;
                const next = bytes[end + 1] ?? 0;
                if (next !== 0x00 && !isRestartMarker(next))
                    break;
                end += 2;
            }
        }
        parts.push({ marker, start: offset, end });
        offset = end;
    }
}
/** APP0 as the plain 16-byte JFIF 1.0x header with a 0x0 thumbnail. */
function isPlainJfif(bytes, part) {
    const at = part.start;
    return (part.end - at === 18 &&
        bytes[at + 4] === 0x4a &&
        bytes[at + 5] === 0x46 &&
        bytes[at + 6] === 0x49 &&
        bytes[at + 7] === 0x46 &&
        bytes[at + 8] === 0x00 &&
        bytes[at + 9] === 1 &&
        (bytes[at + 10] ?? 0xff) <= 2 &&
        (bytes[at + 11] ?? 0xff) <= 2 &&
        bytes[at + 16] === 0 &&
        bytes[at + 17] === 0);
}
function refuse(issue) {
    return { ok: false, issue };
}
/**
 * Checks a health card photo before anything stores, scans or shows it. Accepted: at most
 * maxBytes; SOI; an optional plain JFIF APP0 right after it; DQT, DHT and DRI tables; exactly one
 * 8-bit baseline, extended or progressive Huffman frame of 1 or 3 components with each edge in
 * minEdgePx-maxEdgePx; its scans; EOI; and nothing after (no fill bytes either). Everything
 * else is refused, metadata (Exif, GPS, XMP, ICC, comments, thumbnails) included: clients drop
 * it first with stripJpegMetadata. The pixels themselves are not decoded.
 */
export function inspectHealthCardJpeg(bytes) {
    if (bytes.length > HEALTH_CARD_PHOTO_LIMITS.maxBytes)
        return refuse("too_large");
    const walk = walkJpeg(bytes);
    if (!walk.ok)
        return refuse(walk.issue);
    if (walk.filled)
        return refuse("malformed");
    let frame;
    let scans = 0;
    for (const [index, part] of walk.parts.entries()) {
        const { marker, start } = part;
        const length = part.end - start;
        if (marker === SOI || marker === EOI || marker === DQT || marker === DHT)
            continue;
        if (marker === APP0) {
            if (index !== 1 || !isPlainJfif(bytes, part))
                return refuse("metadata");
        }
        else if (isMetadataApp(marker) || marker === COM) {
            return refuse("metadata");
        }
        else if (marker === DRI) {
            if (length !== 6)
                return refuse("malformed");
        }
        else if (FRAME_MARKERS.has(marker)) {
            if (frame !== undefined)
                return refuse("unsupported");
            const components = bytes[start + 9] ?? 0;
            if (length !== 2 + 8 + 3 * components)
                return refuse("malformed");
            if (bytes[start + 4] !== 8 || (components !== 1 && components !== 3)) {
                return refuse("unsupported");
            }
            frame = { height: uint16(bytes, start + 5), width: uint16(bytes, start + 7) };
        }
        else if (marker === SOS) {
            const components = bytes[start + 4] ?? 0;
            const headerLength = uint16(bytes, start + 2);
            if (frame === undefined || components < 1 || components > 4)
                return refuse("malformed");
            if (headerLength !== 6 + 2 * components)
                return refuse("malformed");
            scans += 1;
        }
        else {
            return refuse("unsupported");
        }
    }
    if (frame === undefined || scans === 0)
        return refuse("malformed");
    if (walk.end !== bytes.length)
        return refuse("trailing_data");
    const { minEdgePx, maxEdgePx } = HEALTH_CARD_PHOTO_LIMITS;
    for (const edge of [frame.width, frame.height]) {
        if (edge < minEdgePx || edge > maxEdgePx)
            return refuse("dimensions");
    }
    return { ok: true, width: frame.width, height: frame.height };
}
/**
 * The same JPEG without metadata: APP1-APP15 (Exif, GPS, XMP, ICC, ...), comments, any APP0
 * other than a plain JFIF header right after SOI, 0xFF fill bytes and anything after EOI are
 * dropped; every other segment is kept byte for byte. Null when the bytes are not a JPEG this
 * can walk. It does not check sizes or segments outside the metadata: inspectHealthCardJpeg
 * still decides.
 */
export function stripJpegMetadata(bytes) {
    const walk = walkJpeg(bytes);
    if (!walk.ok)
        return null;
    const kept = [];
    for (const part of walk.parts) {
        const metadata = part.marker === APP0
            ? kept.length !== 1 || !isPlainJfif(bytes, part)
            : isMetadataApp(part.marker) || part.marker === COM;
        if (!metadata)
            kept.push(part);
    }
    const stripped = new Uint8Array(kept.reduce((total, part) => total + part.end - part.start, 0));
    let offset = 0;
    for (const part of kept) {
        stripped.set(bytes.subarray(part.start, part.end), offset);
        offset += part.end - part.start;
    }
    return stripped;
}
// ---------------------------------------------------------------------------
// Patient upload
// ---------------------------------------------------------------------------
/** `POST /v1/public/:clinicSlug/intake/health-card-photo`: the link's token and date of birth. */
export const PublicHealthCardPhotoUploadRequestSchema = z
    .object({
    token: PublicAppointmentTokenSchema,
    dateOfBirth: z.iso.date(),
    /** The JPEG, after stripJpegMetadata, as strict base64. */
    image: HealthCardPhotoBase64Schema,
})
    .strict();
/** The photo was stored for checking. Nothing about it comes back to the patient's page. */
export const PublicHealthCardPhotoReceiptSchema = z.object({ received: z.literal(true) }).strict();
// ---------------------------------------------------------------------------
// What staff see
// ---------------------------------------------------------------------------
/** patient_link: sent from the patient's questionnaire link; staff: taken by staff. */
export const HealthCardPhotoSourceSchema = z.enum(["patient_link", "staff"]);
/**
 * pending_scan: being checked for safety; clean: may be viewed; blocked: the safety check
 * refused it (malware, or bytes that are not a plain photo). Never shown.
 */
export const HealthCardPhotoStatusSchema = z.enum(["pending_scan", "clean", "blocked"]);
/** Whether photos can be sent now: a photo checker reported in recently. */
export const HealthCardPhotoCaptureSchema = z.enum(["available", "unavailable"]);
export const HealthCardPhotoRetentionDaysSchema = z
    .number()
    .int()
    .min(HEALTH_CARD_PHOTO_RETENTION.minDays)
    .max(HEALTH_CARD_PHOTO_RETENTION.maxDays);
/** One photo's metadata (never its bytes). */
export const HealthCardPhotoSchema = z
    .object({
    id: z.string().uuid(),
    source: HealthCardPhotoSourceSchema,
    status: HealthCardPhotoStatusSchema,
    capturedAt: IsoDateTimeSchema,
    /** Who took it: staff photos only. */
    capturedByName: z.string().trim().min(1).max(200).nullable(),
})
    .strict()
    .superRefine((value, context) => {
    if ((value.source === "staff") !== (value.capturedByName !== null)) {
        context.addIssue({
            code: "custom",
            message: "A staff photo names who took it, and only a staff photo",
            path: ["capturedByName"],
        });
    }
});
/**
 * A chart's or a booking's current photos, newest first. A retake replaces the earlier photo
 * from the same place (the same link, or staff on the same chart), so this is usually one row.
 */
export const HealthCardPhotoListSchema = z
    .object({
    capture: HealthCardPhotoCaptureSchema,
    photos: z.array(HealthCardPhotoSchema).max(HEALTH_CARD_PHOTO_LIMITS.listMax),
})
    .strict();
export const HealthCardPhotoDeleteReceiptSchema = z
    .object({ id: z.string().uuid(), deleted: z.literal(true) })
    .strict();
// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------
/**
 * How long the clinic keeps photos: from when staff mark the questionnaire's administrative
 * section reviewed (else from the upload), and for staff photos from capture.
 */
export const HealthCardPhotoSettingsSchema = z
    .object({
    retentionDays: HealthCardPhotoRetentionDaysSchema,
    version: z.number().int().positive(),
    capture: HealthCardPhotoCaptureSchema,
})
    .strict();
export const SaveHealthCardPhotoSettingsRequestSchema = z
    .object({
    expectedVersion: z.number().int().positive(),
    retentionDays: HealthCardPhotoRetentionDaysSchema,
})
    .strict();
//# sourceMappingURL=health-card-photos.js.map